import { useCallback, useEffect, useMemo, useRef } from "react";

type Timer = ReturnType<typeof setTimeout>;
type DebounceFunction = (delay: number, func: () => void) => void;

export interface UseDebounceReturn {
	(delay: number, func: () => void): void;
	debounce: DebounceFunction;
	cancel: () => void;
	flush: () => void;
	readonly hasPendingTimer: boolean;
}

export function useDebounce(): UseDebounceReturn {
	const timer = useRef<Timer | undefined>(undefined);
	const pendingFunc = useRef<(() => void) | undefined>(undefined);
	const hasPendingTimer = useRef(false);

	const cancel = useCallback(() => {
		if (!timer.current) return;
		clearTimeout(timer.current);
		timer.current = undefined;
		pendingFunc.current = undefined;
		hasPendingTimer.current = false;
	}, []);

	const flush = useCallback(() => {
		if (!timer.current) return;
		clearTimeout(timer.current);
		timer.current = undefined;
		hasPendingTimer.current = false;
		const func = pendingFunc.current;
		pendingFunc.current = undefined;
		func?.();
	}, []);

	useEffect(() => {
		return () => {
			if (!timer.current) return;
			clearTimeout(timer.current);
			timer.current = undefined;
			pendingFunc.current = undefined;
		};
	}, []);

	const debounce = useCallback(
		(delay: number, func: () => void) => {
			cancel();
			hasPendingTimer.current = true;
			pendingFunc.current = func;
			timer.current = setTimeout(() => {
				timer.current = undefined;
				hasPendingTimer.current = false;
				pendingFunc.current = undefined;
				func();
			}, delay);
		},
		[cancel],
	);

	return useMemo(() => {
		const callable = ((delay: number, func: () => void) => debounce(delay, func)) as UseDebounceReturn;
		callable.debounce = debounce;
		callable.cancel = cancel;
		callable.flush = flush;
		Object.defineProperty(callable, "hasPendingTimer", {
			get: () => hasPendingTimer.current,
		});
		return callable;
	}, [debounce, cancel, flush]);
}
